import { createAction, props } from "@ngrx/store";
import { FormsInsuranceBenefits } from "@core/models/form-insurance-benefits";
import { OptimizerStep } from "@core/models/http/treatment/treatment-plans/optimizer-dto";
import { InsurancePolicyBenefitOverridden } from "../../../../core/models/http/treatment/insurance-policy-benefits/insurance-policy-benefit-overridden";
import { TreatmentCourtesy } from "../courtesies/models/courtesy";
import { RemoveCourtesy } from "../courtesies/models/remove-courtesy";
import { UpdateCourtesy } from "../courtesies/models/update-courtesy";
import { SelectDiagnosisCode } from "../diagnosis-codes/models/select-diagnosis-code";
import { ExpenseFee } from "../fees/models/expense-fee";
import { RemoveFee } from "../fees/models/remove-fee";
import { SelectTreatment } from "../fees/models/select-treatment";
import { SelectTreatmentOption } from "../fees/models/select-treatment-option";
import { TreatmentFee } from "../fees/models/treatment-fee";
import { TreatmentOptionFee } from "../fees/models/treatment-option-fee";
import { UpdateFee } from "../fees/models/update-fee";
import { AssignmentOfBenefitChanged } from "../insurance-policies/assignment-of-benefit-changed";
import { ExamChanged } from "../patient-exams/exam-changed";
import { UpdateNetworkDiscount } from "../treatment-plan-benefits/update-network-discount";
import { CreateTreatmentPlan } from "../treatment-plans/models/create-treatment-plan";
import { TreatmentPlan } from "../treatment-plans/models/treatment-plan";
import { UpdateAcceledent } from "../treatment-plans/models/update-acceledent";
import { UpdateTreatmentLength } from "../treatment-plans/models/update-treatment-length";
import { UpdateTreatmentPlanName } from "../treatment-plans/models/update-treatment-plan-name";
import { UpdateLineItem } from "../line-items/update-line-item";

export const loadContract = createAction(
  "[Treatment Page] Load Contract",
  props<{ contractId: number }>()
);

export const insuranceInvalidated = createAction(
  "[Treatment Page] Insurance Invalidated"
);

export const continueToSlider = createAction(
  "[Treatment Page] Continue To Slider",
  props<{ contractId: number }>()
);

export const feeModalEntered = createAction(
  "[Treatment Page] Fee Modal Opened"
);

export const feeDetailModalEntered = createAction(
  "[Treatment Page] Fee Detail Modal Opened"
);

export const courtesyModalEntered = createAction(
  "[Treatment Page] Courtesy Modal Opened"
);

export const insurancePolicySelected = createAction(
  "[Treatment Page] Insurance Policy Selected",
  props<{ tabIndex: number }>()
);

export const diagnosisCodeSelected = createAction(
  "[Treatment Page] Diagnosis Code Selected",
  props<SelectDiagnosisCode>()
);

export const patientExamUpdated = createAction(
  "[Treatment Page] Update Patient Exam",
  props<{ exam: ExamChanged }>()
);

export const courtesyCreated = createAction(
  "[Courtesy Page] Create Courtesy",
  props<{ courtesy: TreatmentCourtesy }>()
);

export const courtesyUpdated = createAction(
  "[Treatment Page] Courtesy Updated",
  props<UpdateCourtesy>()
);

export const courtesyRemoved = createAction(
  "[Treatment Page] Courtesy Removed",
  props<RemoveCourtesy>()
);

export const createExpense = createAction(
  "[Treatment Page] Create Expense",
  props<{ fee: ExpenseFee }>()
);

export const createExpenses = createAction(
  "[Treatment Page] Create Expenses",
  props<{ fees: ExpenseFee[] }>()
);

export const feeUpdated = createAction(
  "[Treatment Page] Fee Updated",
  props<UpdateFee>()
);

export const expenseRemoved = createAction(
  "[Treatment Page] Expense Removed",
  props<RemoveFee>()
);

export const networkDiscountUpdated = createAction(
  "[Treatment Page] Network Discount Updated",
  props<UpdateNetworkDiscount>()
);

export const assignmentOfBenefitChanged = createAction(
  "[Treatment Page] AOB Changed",
  props<AssignmentOfBenefitChanged>()
);

export const treatmentPlanSelected = createAction(
  "[Treatment Page] Treatment Plan Selected",
  props<{ index: number }>()
);

export const treatmentPlanCreated = createAction(
  "[Treatment Page] Treatment Plan Created",
  props<{ treatmentPlan: CreateTreatmentPlan }>()
);

export const treatmentPlanTemplateAdded = createAction(
  "[Treatment Page] Treatment Plan Template Added",
  props<{
    treatmentFee: TreatmentFee;
    treatmentOptionFee: TreatmentOptionFee;
    treatmentPlan: TreatmentPlan;
  }>()
);

export const treatmentPlanRemoved = createAction(
  "[Treatment Page] Treatment Plan Removed",
  props<{ id: number }>()
);

export const duplicateTreatmentPlan = createAction(
  "[Treatment Page] Duplicate Treatment Plan",
  props<{ id: number }>()
);

export const treatmentLengthChanged = createAction(
  "[Treatment Page] Treatment Length Changed",
  props<UpdateTreatmentLength>()
);

export const treatmentNameChanged = createAction(
  "[Treatment Page] Treatment Name Changed",
  props<UpdateTreatmentPlanName>()
);

export const acceledentChanged = createAction(
  "[Treatment Page] Acceledent Changed",
  props<UpdateAcceledent>()
);

export const treatmentSelected = createAction(
  "[Treatment Page] Treatment Selected",
  props<SelectTreatment>()
);

export const treatmentOptionSelected = createAction(
  "[Treatment Page] Treatment Option Selected",
  props<SelectTreatmentOption>()
);

export const benefitOverridden = createAction(
  "[Treatment Page] Benefit Overridden",
  props<InsurancePolicyBenefitOverridden>()
);

export const lineItemUpdated = createAction(
  "[Treatment Page] Line Item Updated",
  props<UpdateLineItem>()
);

export const additionalCostCompleted = createAction(
  "[Treatment Page] Additional Cost Completed or Skipped",
  props<{
    treatmentPlanId: number;
    selectedAdditionalCosts: number[];
    selectedIncludableCosts: number[];
    optimizerStep: OptimizerStep;
  }>()
);

/**
 * Signal for the creation of a simplified insurance policy for use with OrthoFi
 * Yes! instead of the full insurance services.
 */
export const createInsurancePolicy = createAction(
  "[Treatment Page] Add Policy to OrthoFi Yes! Estimated Benefits Table",
  props<FormsInsuranceBenefits>()
);

/**
 * Signal for the removal a simplified insurance policy that was created for use
 * with OrthoFi Yes! instead of the full insurance services.
 */
export const removeInsurancePolicy = createAction(
  "[Treatment Page] Remove Policy from OrthoFi Yes! Estimated Benefits Table",
  props<{ insurancePolicyId: number; treatmentPlanId: number }>()
);
